import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Eye, MoreHorizontal, Shield, Mail, Phone, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { systemUsers, permissionGroups } from '../data/mockData';
import { SystemUser } from '../types';
export function UserManagement() {
  const [users, setUsers] = useState<SystemUser[]>(systemUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  // Form State
  const [formData, setFormData] = useState<Partial<SystemUser>>({
    name: '',
    name_en: '',
    mobile: '',
    email: '',
    username: '',
    password: '',
    permission_group_id: '',
    status: 'active'
  });
  const filteredUsers = users.filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.name_en.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.username.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleCreateOpen = () => {
    setFormData({
      name: '',
      name_en: '',
      mobile: '',
      email: '',
      username: '',
      password: '',
      permission_group_id: permissionGroups[0]?.id || '',
      status: 'active'
    });
    setIsCreateModalOpen(true);
  };
  const handleEditOpen = (user: SystemUser) => {
    setSelectedUser(user);
    setFormData({
      ...user,
      password: ''
    }); // Don't show password
    setIsEditModalOpen(true);
  };
  const handleDetailsOpen = (user: SystemUser) => {
    setSelectedUser(user);
    setIsDetailsModalOpen(true);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(u => u.id !== id));
    }
  };
  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newUser: SystemUser = {
      ...(formData as SystemUser),
      id: `u${Date.now()}`,
      created_at: new Date().toISOString().split('T')[0]
    };
    setUsers([newUser, ...users]);
    setIsCreateModalOpen(false);
  };
  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    const updatedUsers = users.map(u => u.id === selectedUser.id ? {
      ...u,
      ...formData
    } as SystemUser : u);
    setUsers(updatedUsers);
    setIsEditModalOpen(false);
  };
  const getGroupName = (id: string) => {
    const group = permissionGroups.find(g => g.id === id);
    return group ? group.name_en : 'Unknown';
  };
  return <div className="space-y-6" data-id="element-393">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-394">
        <div data-id="element-395">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-396">User Management</h1>
          <p className="text-slate-500" data-id="element-397">
            Manage system users and their access roles
          </p>
        </div>
        <Button onClick={handleCreateOpen} data-id="element-398">
          <Plus className="mr-2 h-4 w-4" data-id="element-399" /> Add New User
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-col sm:flex-row gap-4" data-id="element-400">
        <div className="relative flex-1" data-id="element-401">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-402" />
          <input type="text" placeholder="Search by name, email, or username..." className="w-full h-10 pl-9 pr-4 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-403" />
        </div>
        <div className="w-full sm:w-48" data-id="element-404">
          <Select options={[{
          value: 'all',
          label: 'All Roles'
        }, ...permissionGroups.map(g => ({
          value: g.id,
          label: g.name_en
        }))]} data-id="element-405" />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden" data-id="element-406">
        <div className="overflow-x-auto" data-id="element-407">
          <table className="w-full text-sm text-left" data-id="element-408">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-409">
              <tr data-id="element-410">
                <th className="px-6 py-3" data-id="element-411">Name</th>
                <th className="px-6 py-3" data-id="element-412">Contact Info</th>
                <th className="px-6 py-3" data-id="element-413">Username</th>
                <th className="px-6 py-3" data-id="element-414">Role</th>
                <th className="px-6 py-3" data-id="element-415">Status</th>
                <th className="px-6 py-3 text-right" data-id="element-416">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-417">
              {filteredUsers.length > 0 ? filteredUsers.map(user => <tr key={user.id} className="hover:bg-slate-50 transition-colors" data-id="element-418">
                    <td className="px-6 py-4" data-id="element-419">
                      <div className="font-medium text-slate-900" data-id="element-420">
                        {user.name_en}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-421">{user.name}</div>
                    </td>
                    <td className="px-6 py-4" data-id="element-422">
                      <div className="flex items-center text-slate-600 mb-1" data-id="element-423">
                        <Mail className="h-3 w-3 mr-1.5" data-id="element-424" /> {user.email}
                      </div>
                      <div className="flex items-center text-slate-600" data-id="element-425">
                        <Phone className="h-3 w-3 mr-1.5" data-id="element-426" /> {user.mobile}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-427">
                      {user.username}
                    </td>
                    <td className="px-6 py-4" data-id="element-428">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-429">
                        {getGroupName(user.permission_group_id)}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-430">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-431">
                        {user.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-432">
                      <div className="flex items-center justify-end space-x-2" data-id="element-433">
                        <button onClick={() => handleDetailsOpen(user)} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="View Details" data-id="element-434">
                          <Eye size={16} data-id="element-435" />
                        </button>
                        <button onClick={() => handleEditOpen(user)} className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors" title="Edit User" data-id="element-436">
                          <Edit2 size={16} data-id="element-437" />
                        </button>
                        <button onClick={() => handleDelete(user.id)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete User" data-id="element-438">
                          <Trash2 size={16} data-id="element-439" />
                        </button>
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-440">
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-500" data-id="element-441">
                    No users found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center text-sm text-slate-500" data-id="element-442">
          <div data-id="element-443">
            Showing {filteredUsers.length} of {users.length} users
          </div>
          <div className="flex space-x-2" data-id="element-444">
            <button className="px-3 py-1 border border-slate-300 rounded hover:bg-white disabled:opacity-50" disabled data-id="element-445">
              Previous
            </button>
            <button className="px-3 py-1 border border-slate-300 rounded hover:bg-white disabled:opacity-50" disabled data-id="element-446">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Add New User" size="lg" data-id="element-447">
        <form onSubmit={handleCreateSubmit} className="space-y-4" data-id="element-448">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-449">
            <Input label="Name (English)" value={formData.name_en} onChange={e => setFormData({
            ...formData,
            name_en: e.target.value
          })} required data-id="element-450" />
            <Input label="Name (Bengali)" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} required data-id="element-451" />
            <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} required data-id="element-452" />
            <Input label="Mobile Number" value={formData.mobile} onChange={e => setFormData({
            ...formData,
            mobile: e.target.value
          })} required data-id="element-453" />
            <Input label="Username" value={formData.username} onChange={e => setFormData({
            ...formData,
            username: e.target.value
          })} required data-id="element-454" />
            <Input label="Password" type="password" value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} required data-id="element-455" />
            <Select label="Permission Group" options={permissionGroups.map(g => ({
            value: g.id,
            label: g.name_en
          }))} value={formData.permission_group_id} onChange={e => setFormData({
            ...formData,
            permission_group_id: e.target.value
          })} data-id="element-456" />
            <Select label="Status" options={[{
            value: 'active',
            label: 'Active'
          }, {
            value: 'inactive',
            label: 'Inactive'
          }]} value={formData.status} onChange={e => setFormData({
            ...formData,
            status: e.target.value as 'active' | 'inactive'
          })} data-id="element-457" />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 mt-4" data-id="element-458">
            <Button type="button" variant="secondary" onClick={() => setIsCreateModalOpen(false)} data-id="element-459">
              Cancel
            </Button>
            <Button type="submit" data-id="element-460">Create User</Button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit User" size="lg" data-id="element-461">
        <form onSubmit={handleEditSubmit} className="space-y-4" data-id="element-462">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-463">
            <Input label="Name (English)" value={formData.name_en} onChange={e => setFormData({
            ...formData,
            name_en: e.target.value
          })} required data-id="element-464" />
            <Input label="Name (Bengali)" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} required data-id="element-465" />
            <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} required data-id="element-466" />
            <Input label="Mobile Number" value={formData.mobile} onChange={e => setFormData({
            ...formData,
            mobile: e.target.value
          })} required data-id="element-467" />
            <Input label="Username" value={formData.username} onChange={e => setFormData({
            ...formData,
            username: e.target.value
          })} required data-id="element-468" />
            <Input label="Password (Leave blank to keep current)" type="password" value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} data-id="element-469" />
            <Select label="Permission Group" options={permissionGroups.map(g => ({
            value: g.id,
            label: g.name_en
          }))} value={formData.permission_group_id} onChange={e => setFormData({
            ...formData,
            permission_group_id: e.target.value
          })} data-id="element-470" />
            <Select label="Status" options={[{
            value: 'active',
            label: 'Active'
          }, {
            value: 'inactive',
            label: 'Inactive'
          }]} value={formData.status} onChange={e => setFormData({
            ...formData,
            status: e.target.value as 'active' | 'inactive'
          })} data-id="element-471" />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 mt-4" data-id="element-472">
            <Button type="button" variant="secondary" onClick={() => setIsEditModalOpen(false)} data-id="element-473">
              Cancel
            </Button>
            <Button type="submit" data-id="element-474">Save Changes</Button>
          </div>
        </form>
      </Modal>

      {/* User Details Modal */}
      <Modal isOpen={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} title="User Details" data-id="element-475">
        {selectedUser && <div className="space-y-6" data-id="element-476">
            <div className="flex items-center space-x-4 pb-6 border-b border-slate-100" data-id="element-477">
              <div className="h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-2xl font-bold" data-id="element-478">
                {selectedUser.name_en.charAt(0)}
              </div>
              <div data-id="element-479">
                <h3 className="text-xl font-bold text-slate-900" data-id="element-480">
                  {selectedUser.name_en}
                </h3>
                <p className="text-slate-500" data-id="element-481">{selectedUser.name}</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${selectedUser.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-482">
                  {selectedUser.status === 'active' ? 'Active Account' : 'Inactive Account'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-483">
              <div data-id="element-484">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-485">
                  Username
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-486">
                  {selectedUser.username}
                </p>
              </div>
              <div data-id="element-487">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-488">
                  Role
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-489">
                  {getGroupName(selectedUser.permission_group_id)}
                </p>
              </div>
              <div data-id="element-490">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-491">
                  Email
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-492">
                  {selectedUser.email}
                </p>
              </div>
              <div data-id="element-493">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-494">
                  Mobile
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-495">
                  {selectedUser.mobile}
                </p>
              </div>
              <div data-id="element-496">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-497">
                  Created At
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-498">
                  {selectedUser.created_at}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-slate-100" data-id="element-499">
              <Button variant="secondary" onClick={() => setIsDetailsModalOpen(false)} data-id="element-500">
                Close
              </Button>
              <Button onClick={() => {
            setIsDetailsModalOpen(false);
            handleEditOpen(selectedUser);
          }} data-id="element-501">
                Edit User
              </Button>
            </div>
          </div>}
      </Modal>
    </div>;
}